import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  addEmailService,
  addSmsService,
  approveService,
  freezeUserService,
  getAccountStatementService,
  getDuplicateUsersService,
  getNonUSUsersService,
  getNotificationsServiceList,
  getNotificationUserServiceList,
  getUserDetailsService,
  getUserInvestedPropertiesService,
  getUserSavedPropertiesService,
  getUsersService,
  manualKYCService,
  resetPinPasswordService,
} from '../../services/users.service';

const initialState = {
  usersList: {
    totalCount: 0,
    totalPage: 0,
    usersData: [],
  },
  userDetails: null,
  propertiesList: {
    totalCount: 0,
    totalPage: 0,
    propertiesData: [],
  },
  accountStatementList: {
    totalCount: 0,
    totalPage: 0,
    statementsData: [],
  },
  notificationsService: {
    totalCount: 0,
    totalPage: 0,
    notificationsData: [],
  },
  users: {
    totalCount: 0,
    totalPage: 0,
    currentPage: 0,
    notificationsUserListData: [],
  },
};

export const getUsersList = createAsyncThunk(
  `GET_USERS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      userKYCStatus,
      userInvestmentStatus,
      regulationStatus,
      search,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getUsersService(
        page,
        limit,
        sortField,
        sortType,
        userKYCStatus,
        userInvestmentStatus,
        regulationStatus,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getNonUSUsersList = createAsyncThunk(
  `GET_NON_US_USERS`,
  async ({ page, limit, sortField, sortType, search }, { rejectWithValue }) => {
    try {
      const response = await getNonUSUsersService(
        page,
        limit,
        sortField,
        sortType,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getDuplicateUsersList = createAsyncThunk(
  `GET_DUPLICATE_USERS`,
  async ({
    page,
    limit,
    sortField,
    sortType,
    userKYCStatus,
    userInvestmentStatus,
    regulationStatus,
    search,
    field,
  }, { rejectWithValue }) => {
    try {
      const response = await getDuplicateUsersService(
        page,
        limit,
        sortField,
        sortType,
        userKYCStatus,
        userInvestmentStatus,
        regulationStatus,
        search,
        field,
      );
      return response
    } catch (error) {
      throw rejectWithValue(error);
    }
  });

export const freezeUserAction = createAsyncThunk(
  `FREEZE_USER`,
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await freezeUserService(id, body);
      return { response, userId: id };
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const freezeUserActionDetails = createAsyncThunk(
  `FREEZE_USER_DETAILS`,
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await freezeUserService(id, body);
      return { response, userId: id };
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);
export const getUserDetails = createAsyncThunk(
  `GET_USER_DETAILS`,
  async ({ userId, onFail = () => { } }, { rejectWithValue }) => {
    try {
      const response = await getUserDetailsService(userId);
      return response;
    } catch (error) {
      onFail();
      throw rejectWithValue(error);
    }
  }
);

export const getUserSavedProperties = createAsyncThunk(
  `GET_SAVED_PROPERTIES`,
  async ({ userId, page, limit, sortField, sortType }, { rejectWithValue }) => {
    try {
      const response = await getUserSavedPropertiesService(
        userId,
        page,
        limit,
        sortField,
        sortType
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getUserInvestedProperties = createAsyncThunk(
  `GET_INVESTED_PROPERTIES`,
  async ({ userId, page, limit, sortField, sortType }, { rejectWithValue }) => {
    try {
      const response = await getUserInvestedPropertiesService(
        userId,
        page,
        limit,
        sortField,
        sortType
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getUserStatement = createAsyncThunk(
  `GET_USER_STATEMENT`,
  async (
    { userId, page, limit, sortField, sortType, startDate, endDate },
    { rejectWithValue }
  ) => {
    try {
      const response = await getAccountStatementService(
        userId,
        page,
        limit,
        sortField,
        sortType,
        startDate,
        endDate
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const activateManualKYC = createAsyncThunk(
  `ACTIVATE_KYC`,
  async (userId, { rejectWithValue }) => {
    try {
      const response = await manualKYCService(userId);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const approveUsers = createAsyncThunk(
  `APPROVE_USER`,
  async (userId, { rejectWithValue }) => {
    try {
      const response = await approveService(userId);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
)

export const getNotificationsList = createAsyncThunk(
  `GET_NOTIFICATION_SERVICE`,
  async (
    { page, limit, type },
    { rejectWithValue }
  ) => {
    try {
      const response = await getNotificationsServiceList(
        page,
        limit,
        type
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getNotificationsUserList = createAsyncThunk(
  'GET_NOTIFICATION_USER_LIST_SERVICE',
  async (
    { page, limit, citizenshipStatus },
    { rejectWithValue }
  ) => {
    try {
      const response = await getNotificationUserServiceList(
        page,
        limit,
        citizenshipStatus,

      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
)

export const addEmail = createAsyncThunk(
  'ADD_EMAIL',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addEmailService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error)
    }
  }
)

export const addSms = createAsyncThunk(
  'ADD_SMS',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addSmsService(body);
      toast.success(`${response?.data?.message}`)
      return response;
    } catch (error) {
      throw rejectWithValue(error)
    }
  }
)

export const resetPinPassword = createAsyncThunk('RESET_PIN_PASSWORD', async ({userId, resetFor}, { rejectWithValue }) => {
  try {
    const response = await resetPinPasswordService(userId, resetFor)
    return response
  } catch (error) {
    throw rejectWithValue(error)
  }
})


const usersSlice = createSlice({
  name: 'usersSlice',
  initialState,
  reducers: {
    resetUsersList: (state) => {
      state.usersList = {
        totalCount: 0,
        totalPage: 0,
        usersData: [],
      };
    },
    resetUserDetails: (state) => {
      state.userDetails = null;
    },
    resetPropertiesList: (state) => {
      state.propertiesList = {
        propertiesData: [],
        totalCount: 0,
        totalPage: 0,
      };
    },
    resetAccountStatements: (state) => {
      state.accountStatementList = {
        totalCount: 0,
        totalPage: 0,
        statementsData: [],
      };
    },
    resetNotificationsService: (state) => {
      state.notificationsService = {
        totalCount: 0,
        totalPage: 0,
        notificationsData: [],
      }
    },
    resetUsers: (state) => {
      state.users = {
        notificationsUserListData: [],
        totalCount: 0,
        currentPage: 0,
        totalPage: 0,
      }

    }
  },
  extraReducers: (builder) => {
    // get US users list
    builder
      .addCase(getUsersList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, userList } = payload?.data?.data;
          state.usersList = {
            totalCount: count,
            totalPage,
            usersData: [...userList],
          };
        }
      })
      .addCase(getUsersList.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // get Non us users list
    builder
      .addCase(getNonUSUsersList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, nonUSUserList } = payload?.data?.data;
          state.usersList = {
            totalCount: count,
            totalPage,
            usersData: [...nonUSUserList],
          };
        }
      })
      .addCase(getNonUSUsersList.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
    
    // get duplicate users list
    builder
      .addCase(getDuplicateUsersList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, userList } = payload?.data?.data ?? [];
          state.usersList = {
            totalCount: count,
            totalPage,
            usersData: [...userList],
          };
        }
      })
      .addCase(getDuplicateUsersList.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // freeze/unfreeze user
    builder
      .addCase(freezeUserAction.fulfilled, (state, { payload }) => {
        if (!payload?.response?.data?.isError) {
          state.usersList = {
            ...state.usersList,
            usersData: state.usersList.usersData.map((user) => {
              return user?.userId === payload?.userId
                ? { ...user, isFreeze: !user?.isFreeze }
                : user;
            }),
          };
          toast.success(`${payload?.response?.data?.message}`);
        }
      })
      .addCase(freezeUserAction.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    //freeze/unfreeze user details
    builder
      .addCase(freezeUserActionDetails.fulfilled, (state, { payload }) => {
        if (!payload?.response?.data?.isError) {
          state.userDetails = {
            ...state.userDetails,
            isFreeze: !current(state.userDetails).isFreeze,
          };
          toast.success(`${payload?.response?.data?.message}`);
        }
      })
      .addCase(freezeUserActionDetails.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      }); 

    // get user details
    builder
      .addCase(getUserDetails.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.userDetails = payload?.data?.data;
        }
      })
      .addCase(getUserDetails.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // get user saved properties
    builder
      .addCase(getUserSavedProperties.fulfilled, (state, { payload }) => {
        if (!payload.data?.isError) {
          const { count, totalPage, savedPropertyList } = payload?.data?.data;
          state.propertiesList = {
            totalCount: count,
            totalPage: totalPage,
            propertiesData: [...savedPropertyList],
          };
        }
      })
      .addCase(getUserSavedProperties.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // get user invested properties
    builder
      .addCase(getUserInvestedProperties.fulfilled, (state, { payload }) => {
        if (!payload.data?.isError) {
          const { count, totalPage, userPropertyList } = payload?.data?.data;
          state.propertiesList = {
            totalCount: count,
            totalPage: totalPage,
            propertiesData: [...userPropertyList],
          };
        }
      })
      .addCase(getUserInvestedProperties.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // get user account statement
    builder
      .addCase(getUserStatement.fulfilled, (state, { payload }) => {
        if (!payload.data?.isError) {
          const { count, totalPage, accountStatementList } =
            payload?.data?.data;
          state.accountStatementList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            statementsData: [...(accountStatementList ?? [])],
          };
        }
      })
      .addCase(getUserStatement.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // approve user's KYC
    builder
      .addCase(activateManualKYC.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.userDetails = {
            ...state.userDetails,
            isKYCVerified: true,
          };
          toast.success(payload?.data?.message);
        }
      })
      .addCase(activateManualKYC.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // approve user service
    builder
      .addCase(approveUsers.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.userDetails = {
            ...state.userDetails,
            isAdminApproved: true,
          };
          toast.success(payload?.data?.message);
        }
      })
      .addCase(approveUsers.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong'
        );
      });
    // get Email service list
    builder
      .addCase(getNotificationsList.fulfilled, (state, { payload }) => {

        if (!payload?.data?.isError) {
          const { count, totalPage, notifications } = payload?.data?.data;
          state.notifications = {
            totalCount: count,
            totalPage,
            notificationsData: [...notifications],
          };
        }
      })
      .addCase(getNotificationsList.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // get Notifications user List

    builder
      .addCase(getNotificationsUserList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, currentPage, nextPage, users } = payload?.data?.data;
          state.users = {
            totalCount: count,
            totalPage,
            currentPage,
            nextPage,
            notificationsUserListData: [...(state.users.notificationsUserListData ?? []), ...users],
          };
        }
      })
      .addCase(getNotificationsUserList.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        )
      })


    // Add Email
    builder
      .addCase(addEmail.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(addEmail.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        )
      })
    
    builder.addCase(resetPinPassword.fulfilled, (state, { payload }) => {
      if (!payload?.data?.isError) {
        toast.success(`${payload?.data?.message}`)
      }
    }).addCase(resetPinPassword.rejected, (state, { payload }) => {
      toast.error(
        payload?.response?.data?.message ?? 'Something went wrong!'
      )
    })
  },
});

export const {
  resetUsersList,
  resetUserDetails,
  resetPropertiesList,
  resetAccountStatements,
  resetNotificationsService,
  resetUsers
} = usersSlice.actions;

export default usersSlice.reducer;
